import { useCallback, useEffect, useRef, useState } from 'react';
import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import { OPERATOR_STATE_REFRESH_EVENT } from '../home/livePublishEvents';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';

const POLL_INTERVAL_MS = 10000;

export function useCloudDeploymentStatus({ enabled = true, shopId: explicitShopId = null } = {}) {
  const t = useLocalizedMessage();
  const { runtime, productProfile, routeShopId } = useShellAppContext();
  const shopId = explicitShopId || routeShopId;
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const isEnabled = Boolean(
    enabled
    && productProfile?.id === 'cloud'
    && shopId
    && runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_DEPLOYMENT_STATUS),
  );

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!isEnabled) {
      setIsLoading(false);
      return null;
    }
    if (!quiet) setIsLoading(true);
    try {
      const response = await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.SETTINGS_V2_DEPLOYMENT_STATUS,
        { shopId },
      );
      if (response?.result === 'error') {
        throw new Error(response.error || t('search.release.statusUnavailable'));
      }
      if (mountedRef.current) {
        setStatus(response);
        setError(null);
      }
      return response;
    } catch (requestError) {
      if (mountedRef.current) {
        setError(requestError?.message || t('search.release.statusUnavailable'));
      }
      return null;
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isEnabled, runtime, shopId, t]);

  useEffect(() => {
    mountedRef.current = true;
    setStatus(null);
    setError(null);
    setIsLoading(isEnabled);
    if (!isEnabled) return undefined;

    refresh();
    const handleRefresh = () => refresh({ quiet: true });
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') handleRefresh();
    }, POLL_INTERVAL_MS);
    window.addEventListener(OPERATOR_STATE_REFRESH_EVENT, handleRefresh);

    return () => {
      mountedRef.current = false;
      window.clearInterval(intervalId);
      window.removeEventListener(OPERATOR_STATE_REFRESH_EVENT, handleRefresh);
    };
  }, [isEnabled, refresh]);

  return { status, isLoading, error, refresh, shopId };
}
